import React, {useCallback, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {isUnnamedWorkspace} from '../utils';

export const DEFAULT_MAX_NUM_CHARS = 40;

export type EditableWorkspaceNameProps = {
  isDisabled: boolean;
  value: string; // the value that's shown when not editing
  debounceWait?: number;
  error?: string;
  maxNumChars?: number;
  initialInputWidth?: number; // this value helps dynamically set the input width
  placeholder?: string;
  setError?: (error?: string) => void;
  checkIsValid?: (newValue: string) => boolean;
  onSave?: (newValue: string) => void;
  onBlur?: () => void;
};

export const EditableWorkspaceName = ({
  isDisabled,
  value,
  error,
  maxNumChars = DEFAULT_MAX_NUM_CHARS,
  initialInputWidth = 8, // default to 8px
  placeholder = '',
  setError,
  checkIsValid,
  onSave,
  onBlur,
}: EditableWorkspaceNameProps) => {
  const [originalValue, setOriginalValue] = useState(value); // Used when canceling
  const [currentValue, setCurrentValue] = useState(value);

  const validateAndSave = useCallback(
    (newValue: string) => {
      if (onSave == null) {
        return;
      }
      const isValid = checkIsValid?.(newValue) ?? true;
      if (newValue !== value && isValid) {
        onSave(newValue);
      }
    },
    [checkIsValid, onSave, value]
  );

  const handleOnBlur = useCallback(() => {
    if (error !== undefined) {
      return;
    }

    // Stop editing
    setOriginalValue(currentValue);
    validateAndSave(currentValue);
    onBlur?.();
  }, [currentValue, error, onBlur, validateAndSave]);

  // Allow user to cancel editing even when errors occur
  const cancelEditing = useCallback(() => {
    setCurrentValue(originalValue);
    validateAndSave(originalValue);
    onBlur?.();
  }, [originalValue, onBlur, validateAndSave]);

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleOnBlur();
      }

      if (e.key === 'Escape') {
        cancelEditing();
      }
    },
    [cancelEditing, handleOnBlur]
  );

  const handleChange = useCallback(
    (e: React.SyntheticEvent) => {
      setError?.(undefined);

      const newValue = (e.target as HTMLInputElement).value;
      if (newValue.length < maxNumChars) {
        setCurrentValue(newValue);
      }
    },
    [setError, maxNumChars]
  );

  const width = getInputWidth(initialInputWidth, currentValue, originalValue);

  // This is a workaround to get the input to dynamically grow based on the input value
  // and placeholder text. https://css-tricks.com/auto-growing-inputs-textareas/
  return (
    <div
      style={{
        width, // tailwind makes it difficult to use arbitrary widths, so forcing an inline style for ease here
      }}
      className={twMerge(
        'text-xl font-semibold',
        'inline-grid items-center border-0 align-top',
        'max-w-[8rem] sm:max-w-[20rem] lg:max-w-[20rem] xl:max-w-[unset] ',
        'after:invisible after:appearance-none after:border-none',
        'after:col-start-1 after:col-end-auto after:row-start-1 after:row-end-auto',
        `after:whitespace-pre-wrap after:content-[attr(data-value)]`
      )}>
      <input
        style={{
          width, // tailwind makes it difficult to use arbitrary widths, so forcing an inline style for ease here
        }}
        autoFocus
        type="text"
        className={twMerge(
          'col-start-1 col-end-auto row-start-1 row-end-auto inline-grid',
          'resize-none appearance-none',
          'max-w-[8rem] sm:max-w-[20rem] lg:max-w-[20rem]  xl:max-w-[unset]',
          'border-0 outline-0',
          'text-xl font-semibold placeholder:text-moon-300'
        )}
        onInput={event => {
          const newValue = (event.target as HTMLInputElement).value;
          const parentNode = (event.target as HTMLInputElement)
            .parentNode as Element;

          // This sets the `data-value` attribute in the parent div element equal to the value of the input.
          // The input is set within a CSS grid, where that grid is a pseudo-element that uses that `data-value` attribute as its content.
          // That content is what stretches the grid to the appropriate size based on the input value.
          if (newValue.length < maxNumChars && parentNode) {
            parentNode.setAttribute('data-value', newValue);
          }
        }}
        size={1} // Force the minimum width of the input
        value={currentValue}
        onChange={handleChange}
        onBlur={handleOnBlur}
        onKeyDown={handleOnKeyDown}
        placeholder={
          isUnnamedWorkspace(originalValue) ? placeholder : originalValue
        }
        onClick={e => e.stopPropagation()}
        disabled={isDisabled}
      />
    </div>
  );
};

function getInputWidth(
  initialInputWidth: number,
  currentValue: string,
  placeholder: string
) {
  if (currentValue === placeholder) {
    return initialInputWidth;
  }
  if (currentValue.length === 0) {
    return initialInputWidth;
  }

  return 'auto';
}
